import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import LinkButton from '@/components/atoms/LinkButton';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useGetBilling from '@/hooks/api/useGetBilling';
import useGetCarriedForwardBilling from '@/hooks/api/useGetCarriedForwardBilling';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import { billingDetailState, notFoundState } from '@/states/billingDetailState';
import { carriedForwardBillingState } from '@/states/carriedForwardBilling';
import { BillingDetailList } from '@/types/api/customer/mercuryMypage';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import { getParamsIfQueryIsString } from '@/utils/common';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';
import { splitYearMonthFromYYYYMM } from '@/utils/date';
import { addCommas, removeCommas } from '@/utils/str';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

/**
 * 旧請求明細方式の利用月に対して、明細表示と請求書ダウンロードを提供するページ。
 */
const Billdetail: NextPage = () => {
  const router = useRouter();
  const billingDetail = useRecoilValue(billingDetailState);
  const carriedForwardBilling = useRecoilValue(carriedForwardBillingState);
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const notFound = useRecoilValue(notFoundState);
  const resetNotFound = useResetRecoilState(notFoundState);
  const resetBillingDetail = useResetRecoilState(billingDetailState);

  // API呼出しhooks
  const { execute: getBilling } = useGetBilling();
  const { execute: getCarriedForwardBilling } = useGetCarriedForwardBilling();

  useEffect(() => {
    if (router.isReady) {
      const { query } = router;
      const yearMonth = getParamsIfQueryIsString(query.month);
      const { year, month } = splitYearMonthFromYYYYMM(yearMonth);

      // 別月からの遷移時に前回の検索結果が残らないよう、参照前に関連 state を初期化する。
      resetNotFound();
      resetBillingDetail();
      getBilling({ year, month });
      getCarriedForwardBilling({ year, month });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  // 請求金額の計算
  const billingDetailInvoiceNum = parseFloat(removeCommas(billingDetail.price_tax_include));
  const carriedForwardBillingNum = parseFloat(
    removeCommas(carriedForwardBilling.carried_forward_billing_info.total_price_tax_included)
  );
  const totalAmount = billingDetailInvoiceNum + carriedForwardBillingNum;
  const formattedTotalAmount = addCommas(totalAmount);

  // 繰越請求額を含めた次回請求予定額を見せるため、対象月の翌月表示を計算する。
  let nextMonth = '';
  const dateObj = new Date(billingDetail.target_month.replace('年', '-').replace('月', '-01'));
  dateObj.setMonth(dateObj.getMonth() + 1);
  nextMonth = `${dateObj.getFullYear()}年${dateObj.getMonth() + 1}月`;

  if (billingDetail && billingDetail?.billing_detail_list.length !== 0 && !notFound) {
    const downloadBillingData = () => {
      (async () => {
        startLoading(ApiUrl.Read.DOWNLOAD_INVOICE);
        // ダウンロード API は YYYY-MM 形式を要求するため、ルーティングクエリをここで整形する。
        const sigV4Headers = await getSigV4HeadersForGet(
          `${ApiUrl.Read.DOWNLOAD_INVOICE}/${router.query.month?.slice(
            0,
            4
          )}-${router.query.month?.slice(4)}`
        );
        await api
          .get(
            `${ApiUrl.Read.DOWNLOAD_INVOICE}/${router.query.month?.slice(
              0,
              4
            )}-${router.query.month?.slice(4)}`,
            {
              headers: sigV4Headers.headers,
            }
          )
          .then(({ data }) => {
            // base64 の PDF をブラウザダウンロードへ変換し、別画面遷移なしで取得できるようにする。
            const buffer = Buffer.from(data.data, 'base64');
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const downloadUrl = URL.createObjectURL(blob);
            const download = document.createElement('a');
            download.href = downloadUrl;
            download.download = data.file_name;
            download.click();
            (window.URL || window.webkitURL).revokeObjectURL(downloadUrl);
          })
          .catch((error: any) => {
            if (isExpiredToken(error)) {
              sessionStorage.clear();
              window.location.href = url('/login');
              return;
            }
            setAlert({
              isOpen: true,
              apiType: '',
            });
          })
          .finally(() => finishLoading(ApiUrl.Read.DOWNLOAD_INVOICE));
      })();
    };
    return (
      <>
        <AnalyticsData />
        <main className='l-main'>
          <Heading as='h1'>ご利用料金明細</Heading>
          <div className='m-box-02'>
            <div className='m-box-02__inner'>
              <div className='m-heading-l2'>
                <h2 className='m-heading-l2__hdg'>{`${billingDetail.target_month}ご利用分`}</h2>
              </div>
              <div className='m-table'>
                <table>
                  <colgroup>
                    <col className='w-50' />
                    <col className='w-30' />
                    <col className='w-20' />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className='m-table__th'>ご利用明細</th>
                      <th className='m-table__th'>金額(税込)</th>
                      <th className='m-table__th'>税率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingDetail.billing_detail_list.map((data: BillingDetailList) => (
                      <tr key={data.name}>
                        <th className='m-table__th'>{data.name}</th>
                        <td className='m-table__td amount'>&yen;{data.price}</td>
                        <td className='m-table__td amount'>{data.tax}</td>
                      </tr>
                    ))}
                    <tr>
                      <th className='m-table__th'>合計</th>
                      <td className='m-table__td amount'>&yen;{billingDetail.price_tax_include}</td>
                      <td className='m-table__td' />
                    </tr>
                  </tbody>
                </table>
                {totalAmount ? (
                  <>
                    <div className='m-heading-l2'>
                      {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                      <h2 className='m-heading-l2__hdg' />
                    </div>
                    <table style={{ marginTop: '20px' }}>
                      <colgroup>
                        <col className='w-50' />
                        <col className='w-30' />
                        <col className='w-20' />
                      </colgroup>
                      <tbody>
                        <tr>
                          <th className='m-table__th'>{`${nextMonth}度請求金額`}</th>
                          <td className='m-table__td amount' style={{ width: '100%' }}>
                            &yen;{formattedTotalAmount}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table style={{ marginTop: '20px' }}>
                      <colgroup>
                        <col className='w-50' />
                        <col className='w-30' />
                        <col className='w-20' />
                      </colgroup>
                      <tbody>
                        <tr>
                          <th className='m-table__th'>繰越請求額</th>
                          <td className='m-table__td amount' style={{ width: '100%' }}>
                            &yen;
                            {
                              carriedForwardBilling.carried_forward_billing_info
                                .total_price_tax_included
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                ) : null}
              </div>
              <div className='m-button'>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '32px',
                  }}
                >
                  {billingDetail.billing_dl_publication_flag && (
                    <Button
                      disabled={!billingDetail.billing_dl_publication_flag}
                      type='button'
                      variant='secondary'
                      onClick={downloadBillingData}
                    >
                      請求書ダウンロード
                    </Button>
                  )}
                  <LinkButton variant='secondary' href='/top/history/' style={{ padding: 0 }}>
                    ご利用料金・お支払い状況一覧
                  </LinkButton>
                  <BackLinkButton href='/top'>メニューへ戻る</BackLinkButton>
                </div>
              </div>
            </div>
          </div>
          <BreadcrumbLists />
        </main>
      </>
    );
  }
  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ご利用料金明細</Heading>
        {notFound && <strong>表示可能な情報が存在しません</strong>}
      </main>
    </>
  );
};

export default Billdetail;
